import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import dynamicContentService from '@/services/dynamicContentService';

interface SupportContact {
  value: string;
  description: string;
}

interface SupportContacts {
  [key: string]: SupportContact;
}

const SupportContactsPage: React.FC = () => {
  const [contacts, setContacts] = useState<SupportContacts>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await dynamicContentService.getSupportContacts();

      if (result.success) {
        setContacts(result.data);
      } else {
        setError(result.error || 'Failed to fetch support contacts');
      }
    } catch (err: any) {
      console.error('Error fetching support contacts:', err);
      setError(err.response?.data?.error || 'Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      // Extract just the values for the API
      const updates: { [key: string]: string } = {};
      Object.entries(contacts).forEach(([key, contact]) => {
        updates[key] = contact.value;
      });

      const result = await dynamicContentService.updateSupportContacts(updates);

      if (result.success) {
        setSuccess('Support contacts updated successfully');
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(result.error || 'Failed to update support contacts');
      }
    } catch (err: any) {
      console.error('Error saving support contacts:', err);
      setError(err.response?.data?.error || 'Network error. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (key: string, value: string) => {
    setContacts(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        value
      }
    }));
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
        Support Contact Information
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Grid container spacing={3}>
            {Object.entries(contacts).map(([key, contact]) => (
              <Grid item xs={12} md={6} key={key}>
                <TextField
                  fullWidth
                  label={contact.description || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  value={contact.value || ''}
                  onChange={(e) => handleChange(key, e.target.value)}
                  variant="outlined"
                  helperText={contact.description}
                />
              </Grid>
            ))}
          </Grid>

          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default SupportContactsPage;
